import { Component, OnInit, OnDestroy, Input, signal } from '@angular/core';
import { UdtService } from '../../../shared/services/udt.service';
import { PeriforOnChangeMessages, SignalRService } from '../../../shared/services/signal-r.service';
import { Observable, catchError, forkJoin, map } from 'rxjs';
import { WellTypeService } from '../../services/well-type-datums.service';
import { FormationTopService } from '../../services/formation-top.service';
import { FormationTop } from 'src/app/perical/models/formation.model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { Store } from '@ngneat/elf';
import { UdtPlotUi } from '../../models/undisturbed-temperature.model';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';

@Component({
  selector: 'app-udt-plot',
  template: `
    <app-tool-loader [isLoading]="isLoading()" [positionUnset]="true">
      <p-toolbar>
        <ng-template #start>
          <div class="mr-2">
            <p-checkbox inputId="plotFormationTops" size="small" [(ngModel)]="plotFormationTops" (onChange)="toggleFormationTops($event)" binary="true">
            </p-checkbox>
            <label class="ml-2" for="plotFormationTops">Show Formation Tops</label>
          </div>
        </ng-template>
      </p-toolbar>

      <app-xy-line-plot
        [plotData]="plot.data"
        [xAxisTitle]="xAxisTitle"
        [yAxisTitle]="yAxisTitle"
        [plotName]="plotName"
        [downloadPlotName]="downloadPlotName"
        [plotTitle]="plotName"
        [xyLinePlotStore]="udtPlotStore"
      ></app-xy-line-plot>

      <p-dialog
        [visible]="displayDialog()"
        [modal]="true"
        [style]="{ width: '50vw' }"
        [breakpoints]="{ '960px': '75vw', '641px': '100vw' }"
        [draggable]="false"
        [resizable]="false"
        [closable]="false"
      >
        <ng-template #header>
          <h1 class="font-bold text-lg">
            <i class="pi pi-exclamation-triangle text-lg mr-3" style="color: yellow"></i>
            Issues Detected
          </h1>
        </ng-template>

        <div class="overflow-y-auto max-h-[60vh] m-2 pr-2">
          <p-card class="mb-4">
            <ng-template pTemplate="header">
              <div class="font-bold text-lg">Validation Issues:</div>
            </ng-template>
            @for (error of errors; track $index) {
              <div class="mb-2 last:mb-0">
                <p>{{ error }}</p>
              </div>
            }
          </p-card>
        </div>

        <ng-template pTemplate="footer">
          <p-button
            label="Close"
            icon="pi pi-times"
            (onClick)="closeDialog()"
          />
        </ng-template>
      </p-dialog>

    </app-tool-loader>
  `,
  standalone: false
})
export class UdtPlotComponent implements OnInit, OnDestroy {

  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.udtPlotStore = this._storeService.createStore(this.componentId, new UdtPlotUi, withUIEntities<XyLinePlotUi>());
    this.plotFormationTops = this.udtPlotStore.state.plotFormationTops;
  }
  get componentId(): string {
    return this._componentId;
  }
  public udtPlotStore: Store;

  private userUnits: UserUnitsModel;
  public plotFormationTops = false;
  public isLoading = signal<boolean>(false);
  public mudlineDepth: number;
  private _plotData: any;
  private _formationTops: FormationTop[]
  public errors: string[] = [];
  public displayDialog = signal<boolean>(false);
  public plot = {
    data: [],
    layout: {},
    config: {}
  };
  public xAxisTitle = '';
  public yAxisTitle = '';
  public plotName = 'UDT Plot';
  public downloadPlotName = 'udt_plot';
  private _waterLineDepth: number;

  constructor(
    private _udtService: UdtService,
    private _signalRService: SignalRService,
    private _wellTypeService: WellTypeService,
    private _formationTopService: FormationTopService,
    private _storeService: StoreService
  ) { }

  async ngOnInit(): Promise<void> {
    this.isLoading.set(true);
    this.userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);

    this.xAxisTitle = `Temperature (${this.userUnits.temperature})`;
    this.yAxisTitle = `Depth (${this.userUnits.longLengths})`;

    this.getData();

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
  }

  private signalRfunc(data: { action: string, designId: string }) {
    if (data.action == PeriforOnChangeMessages.REFRESH_UDT_PLOT || data.action == PeriforOnChangeMessages.REFRESH_TRAJECTORY) {
      this.getData();
    }
  }

  getData() {
    const sources = [
      this._udtService.getUndisturbedTemperaturePlot() as Observable<any>,
      this._formationTopService.getAllFormationTops() as Observable<any>,
      this._wellTypeService.getWellType() as Observable<any>,
    ]

    forkJoin(sources).pipe(
      map(([udtResponse, formationTops, wellType]) => {
        this.mudlineDepth = wellType.waterDepth > 0 ? +(wellType.drillFloorElevation + wellType.waterDepth).toFixed(2) : null;
        this._waterLineDepth = wellType.drillFloorElevation;
        this._formationTops = formationTops;

        // Handle new backend format with data and errors
        if (udtResponse && udtResponse.data) {
          this._plotData = udtResponse.data;
          this.errors = udtResponse.errors || [];
          this.displayDialog.set(this.errors.length > 0);
          this.plotUdt(udtResponse.data);
        } else {
          // Fallback for old format (if still in use)
          this._plotData = udtResponse;
          this.errors = [];
          this.displayDialog.set(false);
          this.plotUdt(udtResponse);
        }
      }),
      catchError(err => {
        this.errors = ['Failed to load UDT plot data'];
        this.displayDialog.set(true);
        this.isLoading.set(false);
        return err;
      })).subscribe();
  }

  plotUdt(plotData) {
    this.plot.data = [];

    // If no data points, just set loading to false and return
    if (!plotData || !plotData.x || plotData.x.length === 0) {
      this.isLoading.set(false);
      return;
    }

    const minX = Math.min(...plotData.x) - 20;
    const maxX = Math.max(...plotData.x) + 20;

    if (this.plotFormationTops) {
      const formationColors: string[] = [
        '#743E07',
        '#9C560F',
        '#D27617',
        '#8E5D2A',
        '#6D4F31',
        '#A36A2F',
        '#EC8316',
        '#A96F34'
      ];

      let i = 0;
      this._formationTops.forEach(formTop => {
        const top = {
          name: formTop.formation['name'] + ' Top',
          y: [formTop.formationTop, formTop.formationTop],
          x: [minX - 0.5, maxX + 0.5],
          mode: 'lines',
          hoverinfo: 'none',
          line: {
            width: 1.5,
            dash: 'dot',
            color: formationColors[i]
          },
        }
        i++;
        if (i >= formationColors.length) { i = 0; }
        this.plot.data.push(top);
      });
    }

    const mudline = {
      name: "Mudline",
      y: [this.mudlineDepth, this.mudlineDepth],
      x: [minX - 0.5, maxX + 0.5],
      line: {
        color: '#783F04'
      },
      mode: 'lines',
      hoverinfo: 'none',
      width: 10
    }

    const msl = {
      name: "MSL",
      y: [this._waterLineDepth, this._waterLineDepth],
      x: [minX - 0.5, maxX + 0.5],
      line: {
        color: '#017BEE'
      },
      mode: 'lines',
      hoverinfo: 'none',
      width: 10
    }

    if (mudline.y[0]) {
      this.plot.data.push(mudline);
      this.plot.data.push(msl);
    }

    const udtData = {
      name: 'UDT',
      x: plotData.x,
      y: plotData.y,
      line: {
        color: '#FF0000'
      },
      visible: true
    }

    this.plot.data.push(udtData);
    this.isLoading.set(false);
  }

  public toggleFormationTops(e) {
    this.plotFormationTops = e.checked;
    this.udtPlotStore.update((state) => ({
      ...state,
      plotFormationTops: e.checked
    }));
    this.plotUdt(this._plotData);
  }

  public closeDialog(): void {
    this.displayDialog.set(false);
  }

  ngOnDestroy() {
    this.signalRfunc = null;
  }
}
